import React, { useState, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, 
  Filter, 
  Download, 
  ChevronLeft, 
  ChevronRight, 
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';
import { FormulaEngine } from './FormulaEngine';

interface Column {
  key: string;
  label: string;
  type?: 'text' | 'number' | 'date' | 'boolean' | 'formula';
  sortable?: boolean;
  filterable?: boolean;
  editable?: boolean;
  width?: string;
  formatter?: (value: any) => string;
}

interface EnhancedDataTableProps {
  data: any[];
  columns: Column[];
  title?: string;
  searchable?: boolean;
  filterable?: boolean;
  sortable?: boolean;
  paginated?: boolean;
  pageSize?: number;
  selectable?: boolean;
  editable?: boolean;
  onDataChange?: (data: any[]) => void;
  onRowSelect?: (selectedRows: any[]) => void;
  onCellEdit?: (rowIndex: number, columnKey: string, newValue: any) => void;
  className?: string;
}

export const EnhancedDataTable: React.FC<EnhancedDataTableProps> = ({
  data,
  columns,
  title = "Data Table",
  searchable = true,
  filterable = true,
  sortable = true,
  paginated = true,
  pageSize = 10,
  selectable = false,
  editable = false,
  onRowSelect,
  onCellEdit,
  className = ""
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState<{key: string, direction: 'asc' | 'desc'} | null>(null);
  const [filters, setFilters] = useState<Record<string, any>>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const [editingCell, setEditingCell] = useState<{row: number, col: string} | null>(null);
  const [editValue, setEditValue] = useState('');
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);

  const formulaEngine = FormulaEngine.getInstance();

  const filteredAndSortedData = useMemo(() => {
    let result = [...data];

    if (searchTerm) {
      result = result.filter(row =>
        Object.values(row).some(value =>
          String(value).toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }

    Object.entries(filters).forEach(([key, filterValue]) => {
      if (filterValue) {
        result = result.filter(row =>
          String(row[key]).toLowerCase().includes(String(filterValue).toLowerCase())
        );
      }
    });

    if (sortConfig) {
      result.sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];
        
        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [data, searchTerm, filters, sortConfig]);

  const paginatedData = useMemo(() => {
    if (!paginated) return filteredAndSortedData;
    
    const startIndex = (currentPage - 1) * pageSize;
    return filteredAndSortedData.slice(startIndex, startIndex + pageSize);
  }, [filteredAndSortedData, currentPage, pageSize, paginated]);

  const totalPages = Math.ceil(filteredAndSortedData.length / pageSize);

  const handleSort = useCallback((columnKey: string) => {
    if (!sortable) return;
    
    setSortConfig(prev => {
      if (prev?.key === columnKey) {
        return prev.direction === 'asc' 
          ? { key: columnKey, direction: 'desc' }
          : null;
      }
      return { key: columnKey, direction: 'asc' };
    });
  }, [sortable]);

  const handleRowSelect = useCallback((rowIndex: number) => {
    if (!selectable) return;
    
    const newSelected = new Set(selectedRows);
    if (newSelected.has(rowIndex)) {
      newSelected.delete(rowIndex);
    } else {
      newSelected.add(rowIndex);
    }
    
    setSelectedRows(newSelected);
    
    if (onRowSelect) {
      const selectedData = Array.from(newSelected).map(index => paginatedData[index]);
      onRowSelect(selectedData);
    }
  }, [selectable, selectedRows, paginatedData, onRowSelect]);

  const handleCellClick = useCallback((rowIndex: number, columnKey: string) => {
    if (!editable) return;
    
    const column = columns.find(col => col.key === columnKey);
    if (!column?.editable) return;
    
    setEditingCell({ row: rowIndex, col: columnKey });
    setEditValue(String(paginatedData[rowIndex][columnKey] || ''));
  }, [editable, columns, paginatedData]);

  const handleCellEdit = useCallback((e: React.KeyboardEvent) => {
    if (!editingCell) return;
    
    if (e.key === 'Enter') {
      const actualRowIndex = (currentPage - 1) * pageSize + editingCell.row;
      
      let processedValue = editValue;
      const column = columns.find(col => col.key === editingCell.col);
      
      if (column?.type === 'formula' || editValue.startsWith('=')) {
        processedValue = formulaEngine.evaluate(editValue, {
          data: filteredAndSortedData,
          currentRow: actualRowIndex,
          currentCol: editingCell.col
        });
      }
      
      if (onCellEdit) {
        onCellEdit(actualRowIndex, editingCell.col, processedValue);
      }
      
      setEditingCell(null);
      setEditValue('');
    } else if (e.key === 'Escape') {
      setEditingCell(null);
      setEditValue('');
    }
  }, [editingCell, editValue, currentPage, pageSize, columns, filteredAndSortedData, formulaEngine, onCellEdit]);

  const renderSortIcon = (columnKey: string) => {
    if (!sortConfig || sortConfig.key !== columnKey) {
      return <ArrowUpDown className="w-4 h-4 text-slate-500" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="w-4 h-4 text-slate-300" />
      : <ArrowDown className="w-4 h-4 text-slate-300" />;
  };

  const renderCell = (row: any, column: Column, rowIndex: number) => {
    const isEditing = editingCell?.row === rowIndex && editingCell?.col === column.key;
    let value = row[column.key];
    
    if (column.formatter && !isEditing) {
      value = column.formatter(value);
    }
    
    if (isEditing) {
      return (
        <input
          type="text"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleCellEdit}
          onBlur={() => setEditingCell(null)}
          className="w-full bg-slate-700/50 border border-slate-500 rounded px-2 py-1 text-white focus:outline-none focus:ring-2 focus:ring-slate-400"
          autoFocus
        />
      );
    }
    
    return (
      <div
        className={`${column.editable ? 'cursor-pointer hover:bg-slate-700/30 rounded px-2 py-1' : ''}`}
        onClick={() => handleCellClick(rowIndex, column.key)}
      >
        {String(value || '')}
      </div>
    );
  };

  return (
    <div className={`enhanced-data-table ${className}`}>
      <div className="table-header">
        <div className="table-controls">
          <div className="flex items-center gap-4">
            <h3 className="text-lg font-semibold text-white">{title}</h3>
            <span className="text-sm text-slate-400">
              {filteredAndSortedData.length} records
            </span>
          </div>
          
          <div className="flex items-center gap-3">
            {searchable && (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input pl-10 w-64"
                />
              </div>
            )}
            
            {filterable && (
              <button
                onClick={() => setIsFilterPanelOpen(!isFilterPanelOpen)}
                className="filter-button"
              >
                <Filter className="w-4 h-4" />
                <span>Filter</span>
              </button>
            )}
            
            <button className="export-button">
              <Download className="w-4 h-4" />
              <span>Export</span>
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {isFilterPanelOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="px-6 py-4 bg-slate-700/30 border-b border-slate-600/50"
          >
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {columns.filter(col => col.filterable !== false).map(column => (
                <div key={column.key}>
                  <label className="block text-sm font-medium text-slate-300 mb-1">
                    {column.label}
                  </label>
                  <input
                    type="text"
                    placeholder={`Filter ${column.label.toLowerCase()}`}
                    value={filters[column.key] || ''}
                    onChange={(e) => setFilters(prev => ({ ...prev, [column.key]: e.target.value }))}
                    className="w-full bg-slate-800/50 border border-slate-600 rounded px-3 py-2 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-500"
                  />
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr>
              {selectable && (
                <th className="w-12 px-4 py-3 bg-slate-700/50 border-b border-slate-600/50">
                  <input
                    type="checkbox"
                    checked={selectedRows.size === paginatedData.length && paginatedData.length > 0}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedRows(new Set(paginatedData.map((_, index) => index)));
                      } else {
                        setSelectedRows(new Set());
                      }
                    }}
                    className="rounded border-slate-500 bg-slate-700 text-slate-400 focus:ring-slate-500"
                  />
                </th>
              )}
              {columns.map(column => (
                <th
                  key={column.key}
                  className={`px-4 py-3 bg-slate-700/50 text-slate-300 font-medium text-left border-b border-slate-600/50 ${
                    column.sortable !== false && sortable ? 'cursor-pointer hover:bg-slate-600/50' : ''
                  }`}
                  style={{ width: column.width }}
                  onClick={() => column.sortable !== false && handleSort(column.key)}
                >
                  <div className="flex items-center gap-2">
                    <span>{column.label}</span>
                    {column.sortable !== false && sortable && renderSortIcon(column.key)}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((row, rowIndex) => (
              <tr
                key={rowIndex}
                className={`hover:bg-slate-700/10 transition-colors ${
                  selectedRows.has(rowIndex) ? 'bg-slate-600/20' : ''
                }`}
              >
                {selectable && (
                  <td className="px-4 py-3 border-b border-slate-700/30">
                    <input
                      type="checkbox"
                      checked={selectedRows.has(rowIndex)}
                      onChange={() => handleRowSelect(rowIndex)}
                      className="rounded border-slate-500 bg-slate-700 text-slate-400 focus:ring-slate-500"
                    />
                  </td>
                )}
                {columns.map(column => (
                  <td
                    key={column.key}
                    className="px-4 py-3 text-slate-200 border-b border-slate-700/30"
                  >
                    {renderCell(row, column, rowIndex)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {paginated && totalPages > 1 && (
        <div className="pagination">
          <div className="pagination-info">
            Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, filteredAndSortedData.length)} of {filteredAndSortedData.length} results
          </div>
          
          <div className="pagination-controls">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="pagination-button"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            
            <span className="px-3 py-1 text-slate-300">
              Page {currentPage} of {totalPages}
            </span>
            
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="pagination-button"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnhancedDataTable;
